export interface LogSorterConfiguration {
  longitude?: number;
  latitude?: number;
  columnPlacement?: LogSorterColumnPlacement;
  numberOfBinsCol1?: number;
  numberOfBinsCol2?: number;
  numberSequence?: LogSorterNumberSequenceCodes;
  distanceX?: number;
  distanceY?: number;
  binDefaultWidth?: number;
  binDefaultLength?: number;
  direction?: number;
  geofenceTop?: number;
  geofenceBottom?: number;
  geofenceOutside?: number;
  geofenceInside?: number;
}

export type LogSorterColumnPlacement = 'Left' | 'Right';

export type LogSorterNumberSequenceCodes =
  | 'Sequential'
  | 'SequentialMirrored'
  | 'Alternating'
  | 'AlternatingMirrored';

// Backwards compatibility
type LegacyLogSorterConfiguration = {
  longitude?: number;
  latitude?: number;
  columns?: '1' | '2R' | '2L';
  numberOfBins?: number;
  numberSequence?: 'Default' | 'Mirrored';
  distanceX?: number;
  distanceY?: number;
  binWidth?: number;
  binLength?: number;
  direction?: number;
  geofenceTop?: number;
  geofenceBottom?: number;
  geofenceOutside?: number;
  geofenceInside?: number;
  geofenceLeft?: number;
  geofenceRight?: number;
};

export function migrateConfiguration(
  config: LogSorterConfiguration | LegacyLogSorterConfiguration,
): LogSorterConfiguration {
  const migrated: LogSorterConfiguration = {
    longitude: config.longitude,
    latitude: config.latitude,
    distanceX: config.distanceX,
    distanceY: config.distanceY,
    direction: config.direction,
    geofenceTop: config.geofenceTop,
    geofenceBottom: config.geofenceBottom,
    geofenceOutside: config.geofenceOutside,
    geofenceInside: config.geofenceInside,
  };

  // -----------------------------
  // columns -> columnPlacement
  // -----------------------------
  if ('columnPlacement' in config && config.columnPlacement != null) {
    migrated.columnPlacement = config.columnPlacement;
  } else if ('columns' in config && config.columns != null) {
    switch (config.columns) {
      case '2L':
        migrated.columnPlacement = 'Left';
        break;
      case '2R':
        migrated.columnPlacement = 'Right';
        break;
      case '1':
      default:
        migrated.columnPlacement = 'Right';
        break;
    }
  }

  // -----------------------------
  // numberSequence migration
  // -----------------------------
  if (config.numberSequence != null) {
    switch (config.numberSequence) {
      case 'Default':
        migrated.numberSequence = 'Alternating';
        break;
      case 'Mirrored':
        migrated.numberSequence = 'AlternatingMirrored';
        break;
      default:
        migrated.numberSequence = config.numberSequence;
        break;
    }
  }

  // -----------------------------
  // renamed bin fields
  // -----------------------------
  if ('binDefaultWidth' in config && config.binDefaultWidth != null) {
    migrated.binDefaultWidth = config.binDefaultWidth;
  } else if ('binWidth' in config && config.binWidth != null) {
    migrated.binDefaultWidth = config.binWidth;
  }

  if ('binDefaultLength' in config && config.binDefaultLength != null) {
    migrated.binDefaultLength = config.binDefaultLength;
  } else if ('binLength' in config && config.binLength != null) {
    migrated.binDefaultLength = config.binLength;
  }

  if (
    'numberOfBinsCol1' in config &&
    config.numberOfBinsCol1 != null &&
    'numberOfBinsCol2' in config &&
    config.numberOfBinsCol2 != null
  ) {
    migrated.numberOfBinsCol1 = config.numberOfBinsCol1;
    migrated.numberOfBinsCol2 = config.numberOfBinsCol2;
  } else if ('numberOfBins' in config && config.numberOfBins != null) {
    const total = config.numberOfBins;

    const col1 = Math.ceil(total / 2);
    const col2 = total - col1;

    migrated.numberOfBinsCol1 = col1;
    migrated.numberOfBinsCol2 = col2;
  }

  return migrated;
}
